<script setup>
import { computed, defineModel } from 'vue';
import { useToggle } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import { vOnClickOutside } from '@vueuse/components';
import {
  subDays,
  endOfDay,
  startOfDay,
  format,
  getUnixTime,
  fromUnixTime,
} from 'date-fns';
import { DATE_RANGE_TYPES } from '../helpers/searchHelper';

import Button from 'dashboard/components-next/button/Button.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';
import DateRangeCalendar from 'dashboard/components/ui/DatePicker/DateRangeCalendar.vue';

const emit = defineEmits(['change']);
const modelValue = defineModel({
  type: Object,
  default: () => ({ type: null, from: null, to: null }),
});

const { t } = useI18n();
const [showDropdown, toggleDropdown] = useToggle();

const DATE_FILTER_ACTIONS = {
  PRESET: 'preset',
};

const PRESET_RANGES = computed(() => [
  {
    label: t('SEARCH.DATE_RANGE.LAST_7_DAYS'),
    value: DATE_RANGE_TYPES.LAST_7_DAYS,
    days: 7,
  },
  {
    label: t('SEARCH.DATE_RANGE.LAST_30_DAYS'),
    value: DATE_RANGE_TYPES.LAST_30_DAYS,
    days: 30,
  },
  {
    label: t('SEARCH.DATE_RANGE.LAST_60_DAYS'),
    value: DATE_RANGE_TYPES.LAST_60_DAYS,
    days: 60,
  },
  {
    label: t('SEARCH.DATE_RANGE.LAST_90_DAYS'),
    value: DATE_RANGE_TYPES.LAST_90_DAYS,
    days: 90,
  },
]);

const computeDateRange = config => {
  const end = endOfDay(new Date());
  const start = startOfDay(subDays(end, config.days));
  return { type: config.value, from: getUnixTime(start), to: getUnixTime(end) };
};

const selectedValue = computed(() => {
  const { from, to, type } = modelValue.value || {};
  if (!from && !to && !type) return '';
  return type || DATE_RANGE_TYPES.BETWEEN;
});

const menuItems = computed(() =>
  PRESET_RANGES.value.map(item => ({
    ...item,
    action: DATE_FILTER_ACTIONS.PRESET,
    isSelected: selectedValue.value === item.value,
  }))
);

const applySelection = ({ type, from, to }) => {
  const newValue = { type, from, to };
  modelValue.value = newValue;
  emit('change', newValue);
};

const clearFilter = () => {
  applySelection({ type: null, from: null, to: null });
  toggleDropdown(false);
};

const handlePresetAction = item => {
  if (selectedValue.value === item.value) {
    clearFilter();
    return;
  }
  applySelection(computeDateRange(item));
  toggleDropdown(false);
};

const handleCalendarSelect = ({ from, to, confirmed }) => {
  applySelection({
    type: DATE_RANGE_TYPES.BETWEEN,
    from: getUnixTime(from),
    to: getUnixTime(to),
  });
  // Close dropdown only after a confirmed selection (single-day re-click or range end)
  if (confirmed) toggleDropdown(false);
};

const formatDate = timestamp => format(fromUnixTime(timestamp), 'MMM d, yyyy');

const selectedLabel = computed(() => {
  const prefix = t('SEARCH.DATE_RANGE.TIME_RANGE');
  if (!selectedValue.value) return prefix;

  const preset = PRESET_RANGES.value.find(p => p.value === selectedValue.value);
  if (preset) return `${prefix}: ${preset.label}`;

  const { from, to } = modelValue.value;
  if (from && to) {
    const fromLabel = formatDate(from);
    const toLabel = formatDate(to);
    return fromLabel === toLabel
      ? `${prefix}: ${fromLabel}`
      : `${prefix}: ${fromLabel} - ${toLabel}`;
  }

  return `${prefix}: ${t('SEARCH.DATE_RANGE.CUSTOM_RANGE')}`;
});

// Pass current custom dates to the calendar for display
const calendarFrom = computed(() => {
  const { type, from } = modelValue.value || {};
  const isPreset = PRESET_RANGES.value.some(p => p.value === type);
  return !isPreset && from ? from : null;
});

const calendarTo = computed(() => {
  const { type, to } = modelValue.value || {};
  const isPreset = PRESET_RANGES.value.some(p => p.value === type);
  return !isPreset && to ? to : null;
});

const hasActiveFilter = computed(() => !!selectedValue.value);
</script>

<template>
  <div
    v-on-click-outside="() => toggleDropdown(false)"
    class="relative flex items-center group min-w-0 max-w-full"
  >
    <Button
      sm
      slate
      :variant="showDropdown ? 'faded' : 'solid'"
      :label="selectedLabel"
      class="group-hover:bg-n-alpha-2 max-w-full"
      trailing-icon
      icon="i-lucide-chevron-down"
      @click="toggleDropdown()"
    />
    <DropdownMenu
      v-if="showDropdown"
      :menu-items="menuItems"
      class="mt-1 ltr:left-0 rtl:right-0 top-full w-80"
      @action="handlePresetAction"
    >
      <template #footer>
        <div class="h-px bg-n-strong" />
        <div class="flex flex-col gap-3 px-1 pb-1">
          <!-- Header row -->
          <div class="flex items-center justify-between h-9">
            <span class="text-sm text-n-slate-11">
              {{ t('SEARCH.DATE_RANGE.CUSTOM_RANGE') }}
            </span>
            <Button
              v-if="hasActiveFilter"
              sm
              ghost
              slate
              icon="i-lucide-x"
              :label="t('SEARCH.DATE_RANGE.CLEAR_FILTER')"
              @click="clearFilter"
            />
          </div>

          <!-- Single-month calendar -->
          <DateRangeCalendar
            :start-date="calendarFrom"
            :end-date="calendarTo"
            @select="handleCalendarSelect"
          />
        </div>
      </template>
    </DropdownMenu>
  </div>
</template>
